/* gg-perf: performance helpers for getglass.us (spec 001-theme-performance). */
(() => {
  const POPUP_TIMEOUT_MS = 5000;

  /* ---------- Product data for colour swatches (FR-3) ---------- */
  const productJsonCache = new Map();
  window.ggCardProductJson = function (card) {
    // Cards that still embed their product data (search, cart, lookbook and bundle cards) keep using it
    const holder = [...card.querySelectorAll('template')]
      .map((template) => template.content.querySelector('[data-json-product]'))
      .find(Boolean);
    if (holder) {
      try {
        return Promise.resolve(JSON.parse(holder.getAttribute('data-json-product')));
      } catch (error) {
        /* unreadable embedded data: fall back to fetching it */
      }
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = card.dataset.productUrl
      ? card.dataset.productUrl.split('?')[0]
      : card.dataset.productHandle ? `${root}products/${card.dataset.productHandle}` : '';
    if (!url) return Promise.resolve(null);
    if (!productJsonCache.has(url)) {
      const request = fetch(`${url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      productJsonCache.set(url, request);
    }
    return productJsonCache.get(url);
  };
  const warmSwatch = (event) => {
    const swatch = event.target && event.target.closest ? event.target.closest('color-swatch') : null;
    const card = swatch && swatch.closest('.card-wrapper');
    if (card) window.ggCardProductJson(card);
  };
  document.addEventListener('pointerover', warmSwatch, { passive: true });
  document.addEventListener('touchstart', warmSwatch, { passive: true });

  /* ---------- Product popups: Quick view and Choose options (FR-3) ---------- */
  const popupCache = new Map();
  const sectionKey = (kind, suffix) => `gg:popup-section:${kind}:${suffix || 'default'}`;
  const readSectionId = (kind, suffix) => { try { return window.sessionStorage.getItem(sectionKey(kind, suffix)); } catch (error) { return null; } };
  const writeSectionId = (kind, suffix, id) => { try { window.sessionStorage.setItem(sectionKey(kind, suffix), id); } catch (error) { /* storage unavailable */ } };

  async function fetchText(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  }

  function popupBaseUrl(kind, productUrl) {
    const path = productUrl.split('?')[0];
    return kind === 'quick_add' ? `${path}?view=quick_add` : path;
  }

  async function loadPopupHtml(kind, productUrl, suffix) {
    const base = popupBaseUrl(kind, productUrl);
    const known = readSectionId(kind, suffix);
    if (known) {
      try {
        const html = await fetchText(`${base}${base.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(known)}`);
        if (html.includes('<product-info')) return html;
      } catch (error) { /* stale section id: fall back to the full page below */ }
    }
    const html = await fetchText(base);
    const match = html.match(/<product-info[^>]*\sdata-section="([^"]+)"/);
    if (match) writeSectionId(kind, suffix, match[1]);
    return html;
  }

  const withTimeout = (promise) => Promise.race([
    promise,
    new Promise((resolve, reject) => setTimeout(() => reject(new Error('timeout')), POPUP_TIMEOUT_MS)),
  ]);
  const escapeAttribute = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

  window.ggProductPopup = {
    load(kind, productUrl, suffix) {
      const key = `${kind}|${productUrl.split('?')[0]}`;
      if (!popupCache.has(key)) {
        const request = withTimeout(loadPopupHtml(kind, productUrl, suffix));
        popupCache.set(key, request);
        request.catch(() => popupCache.delete(key)); // a failed load is retried on the next open
      }
      return popupCache.get(key);
    },
    knowsSection: (kind, suffix) => Boolean(readSectionId(kind, suffix)),
    rememberSection: (kind, suffix, id) => writeSectionId(kind, suffix, id),
    loadingMarkup() {
      return '<div class="gg-popup-status" role="status" aria-live="polite">'
        + '<div class="loading__spinner"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg></div>'
        + '<p>Loading product…</p></div>';
    },
    errorMarkup(productUrl) {
      const href = escapeAttribute((productUrl || '').split('?')[0]);
      return '<div class="gg-popup-status gg-popup-status--error" role="alert">'
        + '<p>Sorry, this product couldn’t be loaded.</p>'
        + `<a class="button" href="${href}">View product page</a></div>`;
    },
  };

  const OPENER = 'modal-opener[data-modal="#QuickViewStandard"] button[data-product-url], modal-opener[data-modal="#QuickAddStandard"] button[data-product-url]';
  const kindOf = (button) => (button.closest('modal-opener').getAttribute('data-modal') === '#QuickAddStandard' ? 'quick_add' : 'quick_view');
  function prefetchFromEvent(event) {
    const button = event.target && event.target.closest ? event.target.closest(OPENER) : null;
    if (!button) return;
    window.ggProductPopup.load(kindOf(button), button.getAttribute('data-product-url'), button.dataset.templateSuffix).catch(() => {});
  }
  document.addEventListener('pointerover', prefetchFromEvent, { passive: true });
  document.addEventListener('touchstart', prefetchFromEvent, { passive: true });
  document.addEventListener('focusin', prefetchFromEvent);

  /* Learn the product section id once per session so later popups fetch ~60 KB instead of the full page. */
  window.ggLearnPopupSections = function (root) {
    const scope = root || document;
    [['quick_view', '#QuickViewStandard'], ['quick_add', '#QuickAddStandard']].forEach(([kind, modal]) => {
      if (window.ggProductPopup.knowsSection(kind, '')) return;
      const button = [...scope.querySelectorAll(`modal-opener[data-modal="${modal}"] button[data-product-url]`)].find((b) => !b.dataset.templateSuffix);
      if (!button) return;
      const run = () => window.ggProductPopup.load(kind, button.getAttribute('data-product-url'), '').catch(() => {});
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 4000 }); else setTimeout(run, 1500);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    const info = document.querySelector('product-info[data-section]');
    if (info && document.body.classList.contains('template-product') && !window.ggTemplateSuffix) {
      window.ggProductPopup.rememberSection('quick_view', '', info.dataset.section);
    }
    window.ggLearnPopupSections(document);
  });
})();

/* ---------- Homepage product rows (FR-6) ---------- */
(() => {
  function reinitProductContent(root) {
    if (typeof window.initWishlistFromLocalStorage === 'function') window.initWishlistFromLocalStorage();
    const compare = document.querySelector('compare-product-popup');
    if (compare && typeof compare.setLocalStorageProductForCompare === 'function') compare.setLocalStorageProductForCompare();
    if (typeof window.initMoreSwatchButtons === 'function') window.initMoreSwatchButtons(root);
    if (window.ggReapplyPriceLock) window.ggReapplyPriceLock();
    if (window.ggPrefetchQuickAdd) window.ggPrefetchQuickAdd();
    if (window.ggLearnPopupSections) window.ggLearnPopupSections(root);
  }

  document.addEventListener('section-fetcher:loaded', (event) => {
    const root = event.target;
    if (root instanceof Element && root.querySelector('.card-wrapper')) reinitProductContent(root);
  });

  document.addEventListener('section-fetcher:error', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.classList.contains('gg-row-pending') || target.querySelector('.gg-row-retry')) return;
    const box = document.createElement(target.tagName === 'UL' ? 'li' : 'div');
    box.className = 'gg-row-retry';
    box.setAttribute('role', 'alert');
    box.innerHTML = '<p>Couldn’t load these products.</p><button type="button" class="button button--secondary">Try again</button>';
    box.querySelector('button').addEventListener('click', () => {
      box.remove();
      const fetcher = document.querySelector(`section-fetcher[data-target-id="${target.id}"]`);
      if (fetcher && typeof fetcher.initialize === 'function') fetcher.initialize();
    });
    target.appendChild(box);
  });
})();

/* ---------- Homepage rows: prefetch their HTML once the page is idle (FR-6) ---------- */
(() => {
  const prefetchRows = () => {
    const run = () => document.querySelectorAll('section-fetcher[data-gg-row]').forEach((fetcher) => {
      if (typeof fetcher.prefetch === 'function') fetcher.prefetch();
    });
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 }); else setTimeout(run, 2000);
  };
  if (document.readyState === 'complete') prefetchRows(); else window.addEventListener('load', prefetchRows, { once: true });
})();
